import {
  GlobalStateRecord,
  useGlobalStateBatch,
} from "@inrange/building-manager-api-client";
import { Icons } from "@inrange/theme-components/icons";
import { useState } from "react";
import { Button, Form, Nav, Table } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

const TABS = [
  { key: "bank_account", label: "Bank Accounts" },
  { key: "terms_and_conditions", label: "Terms & Conditions" },
  { key: "invoice_footer_text", label: "Invoice Footer Text" },
  { key: "vat_notice", label: "VAT Notice" },
  { key: "invoice_questions", label: "Invoice Questions" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const GlobalStateCard = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("bank_account");
  const [isAdding, setIsAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formCol1, setFormCol1] = useState("");
  const [formCol2, setFormCol2] = useState("");
  const [formText, setFormText] = useState("");

  // Single batched fetch of all global-state types; tab switching filters
  // client-side so we don't fire 5 serial /global-state?type=X requests
  // per page load (see ADMIN-APP-83).
  const {
    fetchGlobalState,
    globalStateForType,
    createGlobalState,
    updateGlobalState,
    removeGlobalState,
  } = useGlobalStateBatch();

  const records: GlobalStateRecord[] = globalStateForType(activeTab);

  const resetForm = () => {
    setFormName("");
    setFormCol1("");
    setFormCol2("");
    setFormText("");
    setIsAdding(false);
    setEditId(null);
  };

  const handleStartEdit = (record: GlobalStateRecord) => {
    setEditId(record.id);
    setFormName(record.name);
    if (activeTab === "bank_account") {
      try {
        const parsed = JSON.parse(record.value);
        setFormCol1(parsed.col1 || "");
        setFormCol2(parsed.col2 || "");
      } catch {
        setFormCol1(record.value);
        setFormCol2("");
      }
    } else {
      setFormText(record.value);
    }
    setIsAdding(false);
  };

  const handleSave = () => {
    const value =
      activeTab === "bank_account"
        ? JSON.stringify({ col1: formCol1, col2: formCol2 })
        : formText;

    if (editId) {
      updateGlobalState.mutate(
        { id: editId, type: activeTab, name: formName, value },
        { onSuccess: resetForm }
      );
    } else {
      createGlobalState.mutate(
        { type: activeTab, name: formName, value },
        { onSuccess: resetForm }
      );
    }
  };

  const handleDelete = (record: GlobalStateRecord) => {
    if (record.siteCount > 0 || record.orgCount > 0) {
      window.alert(
        `Cannot delete "${record.name}": referenced by ${record.siteCount} site(s) and ${record.orgCount} organisation(s).`
      );
      return;
    }
    if (window.confirm("Are you sure you want to delete this record?")) {
      removeGlobalState.mutate(record.id);
    }
  };

  const valuePreview = (record: GlobalStateRecord) => {
    if (activeTab === "bank_account") {
      try {
        const parsed = JSON.parse(record.value);
        return `${(parsed.col1 || "").substring(0, 40)}...`;
      } catch {
        return record.value.substring(0, 40) + "...";
      }
    }
    return record.value.substring(0, 80) + (record.value.length > 80 ? "..." : "");
  };

  const renderForm = () => (
    <div className="p-3 border rounded mb-3 bg-light">
      <Form.Group className="mb-2">
        <Form.Label style={{ fontSize: "0.875rem" }}>Name</Form.Label>
        <Form.Control
          type="text"
          size="sm"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="e.g. JP Morgan Chase GBP"
        />
      </Form.Group>
      {activeTab === "bank_account" ? (
        <div className="d-flex gap-3">
          <Form.Group className="flex-fill mb-2">
            <Form.Label style={{ fontSize: "0.875rem" }}>Column 1</Form.Label>
            <Form.Control
              as="textarea"
              size="sm"
              rows={4}
              value={formCol1}
              onChange={(e) => setFormCol1(e.target.value)}
              placeholder="e.g. J.P. Morgan Chase&#10;Swift: CHASBG2L&#10;IBAN: GB30CHAS60924276978325"
            />
          </Form.Group>
          <Form.Group className="flex-fill mb-2">
            <Form.Label style={{ fontSize: "0.875rem" }}>Column 2</Form.Label>
            <Form.Control
              as="textarea"
              size="sm"
              rows={4}
              value={formCol2}
              onChange={(e) => setFormCol2(e.target.value)}
              placeholder="e.g. Beneficiary: InRange LTD&#10;Account: 0076978325&#10;Sort Code: 609242"
            />
          </Form.Group>
        </div>
      ) : (
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: "0.875rem" }}>Value</Form.Label>
          <Form.Control
            as="textarea"
            size="sm"
            rows={6}
            value={formText}
            onChange={(e) => setFormText(e.target.value)}
            placeholder={
              activeTab === "terms_and_conditions"
                ? "Enter terms and conditions text..."
                : activeTab === "vat_notice"
                  ? "Enter VAT notice text..."
                  : activeTab === "invoice_questions"
                    ? "Enter invoice questions / contact text..."
                    : "Enter invoice footer text (e.g. company name, address, phone, VAT number)..."
            }
          />
        </Form.Group>
      )}
      <div className="d-flex gap-2">
        <Button
          variant="success"
          size="sm"
          onClick={handleSave}
          disabled={!formName || createGlobalState.isLoading || updateGlobalState.isLoading}
        >
          {editId ? "Update" : "Create"}
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={resetForm}>
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <CollapsibleCard title="Invoice Configuration" initialOpen={false}>
      <Nav variant="tabs" className="mb-3">
        {TABS.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link
              active={activeTab === tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                resetForm();
              }}
            >
              {tab.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {(isAdding || editId) && renderForm()}

      {!isAdding && !editId && (
        <Button
          variant="outline-primary"
          size="sm"
          className="mb-3"
          onClick={() => setIsAdding(true)}
        >
          + Add new
        </Button>
      )}

      {fetchGlobalState.isLoading ? (
        <div>Loading...</div>
      ) : (
        <div style={{ maxHeight: "330px", overflowY: "auto" }}>
        <Table
          striped
          bordered
          hover
          size="sm"
          style={{ fontSize: 14, marginBottom: 0 }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "var(--bs-body-bg)",
              zIndex: 1,
            }}
          >
            <tr>
              <th>Name</th>
              <th>Value</th>
              <th>Sites</th>
              <th>Orgs</th>
              <th style={{ width: "80px" }}></th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id}>
                <td>{record.name}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{valuePreview(record)}</td>
                <td>{record.siteCount}</td>
                <td>{record.orgCount}</td>
                <td style={{ textAlign: "center" }}>
                  <img
                    alt="edit"
                    src={Icons.edit}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      marginRight: "8px",
                    }}
                    onClick={() => handleStartEdit(record)}
                  />
                  <img
                    alt="delete"
                    src={Icons.trashcanRed}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleDelete(record)}
                  />
                </td>
              </tr>
            ))}
            {records.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        </div>
      )}
    </CollapsibleCard>
  );
};

export default GlobalStateCard;
